'use client';

import { startTransition, useCallback, useEffect, useRef } from 'react';
import { trpc } from '@/app/api/trpc/client';
import { useActivityStore } from '@/app/stores/activityStore';
import { useAnalysisStore } from '@/app/stores/analysisStore';
import { useCoachChatStore } from '@/app/stores/coachChatStore';
import { useCalendarState } from '@/app/calendar/utils/useCalendarState';
import { useSettingsStore } from '@/app/stores/settingsStore';
import { useTimelyAnalysisStore } from '@/app/stores/timelyAnalysisStore';
import { useWorkoutStore } from '@/app/stores/workoutStore';

const SSE_CONFIG_ENDPOINT = '/api/events';

type CoachEventPayload = {
  type: string;
  payload?: Record<string, unknown>;
};

type SseConfig = {
  streamUrl: string;
  accessToken: string;
  impersonationSupportToken?: string;
};

export function SseListener() {
  const utils = trpc.useContext();
  const utilsRef = useRef(utils);
  useEffect(() => {
    utilsRef.current = utils;
  }, [utils]);
  // Use selectors so this component doesn't re-render on every store update.
  const clearWorkouts = useWorkoutStore((s) => s.clearWorkouts);
  const clearActivities = useActivityStore((s) => s.clearActivities);
  const clearAnalyses = useAnalysisStore((s) => s.clearAnalyses);
  const clearTimelyAnalyses = useTimelyAnalysisStore(
    (s) => s.clearTimelyAnalyses
  );
  const setUnreadCount = useCoachChatStore((s) => s.setUnreadCount);
  const setCurrentStatus = useCoachChatStore((s) => s.setCurrentStatus);
  const clearCurrentStatus = useCoachChatStore((s) => s.clearCurrentStatus);
  const setTrainingSettings = useSettingsStore((s) => s.setTrainingSettings);
  const triggerRefresh = useCoachChatStore((s) => s.triggerRefresh);
  const bumpWorkoutRefresh = useWorkoutStore((s) => s.touchWorkouts);
  const bumpActivityRefresh = useActivityStore((s) => s.touchActivities);
  const showChatWindow = useCalendarState((s) => s.showChatWindow);
  // Read inside the event handler via a ref rather than as an effect
  // dependency below: showChatWindow flips every time the user opens/closes
  // the chat window, and putting it in the stream-setup effect's deps would
  // tear down and re-establish the whole SSE connection (including a fresh
  // GET /api/events config fetch) on every toggle.
  const showChatWindowRef = useRef(showChatWindow);
  useEffect(() => {
    showChatWindowRef.current = showChatWindow;
  }, [showChatWindow]);

  useEffect(() => {
    if (!showChatWindow) {
      startTransition(() => {
        clearCurrentStatus();
      });
    }
  }, [showChatWindow, clearCurrentStatus]);

  const invalidateAnalysisState = useCallback(() => {
    const current = utilsRef.current;
    current.analysis.invalidate();
    clearAnalyses();
    clearTimelyAnalyses();
  }, [clearAnalyses, clearTimelyAnalyses]);

  const refreshSettings = useCallback(() => {
    const current = utilsRef.current;
    current.settings.getTrainingSettings.invalidate();
    current.settings.getTrainingSettings
      .fetch()
      .then((settings) => {
        if (settings) {
          setTrainingSettings(settings);
        }
      })
      .catch((error) => {
        console.error('[SSE] Failed to refresh training settings', error);
      });
  }, [setTrainingSettings]);

  const refreshUnreadCount = useCallback(() => {
    const current = utilsRef.current;
    if (current.coachChat.getUnreadCount?.fetch) {
      current.coachChat.getUnreadCount
        .fetch()
        .then((data) => {
          if (data && typeof data.count === 'number') {
            setUnreadCount(data.count);
          }
        })
        .catch((error) => {
          console.warn('[SSE] Failed to refresh unread count', error);
        });
    }
  }, [setUnreadCount]);

  const invalidateChats = useCallback(() => {
    const current = utilsRef.current;
    current.coachChat.getMessages?.invalidate();
    current.coachChat.getUnreadCount?.invalidate?.();
  }, []);

  useEffect(() => {
    let source: EventSource | null = null;
    let aborted = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    const clearCaches = () => {
      clearWorkouts();
      clearActivities();
    };

    const handleEvent = (event: MessageEvent) => {
      if (!event.data) return;

      let parsed: CoachEventPayload;
      try {
        parsed = JSON.parse(event.data);
      } catch (error) {
        console.warn('[SSE] Unable to parse payload', event.data, error);
        return;
      }

      const { type, payload } = parsed;

      switch (type) {
        case 'workout_changed':
          utilsRef.current.workouts.invalidate();
          invalidateAnalysisState();
          clearCaches();
          bumpWorkoutRefresh();
          break;
        case 'activity_changed':
          utilsRef.current.activities.invalidate();
          invalidateAnalysisState();
          clearCaches();
          bumpActivityRefresh();
          break;
        case 'analysis_completed':
          utilsRef.current.workouts.invalidate();
          invalidateAnalysisState();
          clearCaches();
          bumpWorkoutRefresh();
          break;
        case 'plan_changed':
          utilsRef.current.planInstance.getActive.invalidate();
          utilsRef.current.workouts.invalidate();
          refreshSettings();
          invalidateAnalysisState();
          clearCaches();
          bumpWorkoutRefresh();
          break;
        case 'race_target_changed':
          utilsRef.current.settings.getRaceTarget.invalidate();
          utilsRef.current.planInstance.getActive.invalidate();
          invalidateAnalysisState();
          clearCaches();
          bumpWorkoutRefresh();
          break;
        case 'training_zones_changed':
          refreshSettings();
          invalidateAnalysisState();
          bumpWorkoutRefresh();
          break;
        case 'onboarding_completed':
          refreshSettings();
          invalidateAnalysisState();
          clearCaches();
          break;
        case 'onboarding_resume':
          // Optional: handle if we want to force UI to onboarding view
          break;
        case 'chat_unread_updated':
          if (showChatWindowRef.current) {
            setUnreadCount(0);
          } else if (typeof payload?.unreadCount === 'number') {
            setUnreadCount(payload.unreadCount);
          } else {
            refreshUnreadCount();
          }
          invalidateChats();
          break;
        case 'chat_message_created':
          if (showChatWindowRef.current) {
            setUnreadCount(0);
          } else if (typeof payload?.unreadCount === 'number') {
            setUnreadCount(payload.unreadCount);
          } else {
            refreshUnreadCount();
          }
          invalidateChats();
          triggerRefresh();
          break;
        case 'coach_status':
          if (showChatWindowRef.current) {
            const message = payload?.message;
            if (typeof message === 'string' && message.trim()) {
              startTransition(() => {
                setCurrentStatus(message);
              });
            }
          }
          break;
        default:
          console.debug('[SSE] Unhandled event type', type);
      }
    };

    const scheduleReconnect = () => {
      if (aborted) {
        return;
      }
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
      }
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        startStream();
      }, 5000);
    };

    const handleError = (error: Event) => {
      console.warn('[SSE] event stream error', error);
      cleanupSource();
      scheduleReconnect();
    };

    const handleOpen = () => {
      console.debug('[SSE] connected to coach events');
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
    };

    const cleanupSource = () => {
      if (source) {
        source.removeEventListener('message', handleEvent);
        source.removeEventListener('error', handleError);
        source.removeEventListener('open', handleOpen);
        source.close();
        source = null;
      }
    };

    const startStream = async () => {
      try {
        if (aborted) {
          return;
        }
        const response = await fetch(SSE_CONFIG_ENDPOINT, {
          cache: 'no-store',
        });
        if (!response.ok) {
          throw new Error(
            `[SSE] Failed to fetch stream config (${response.status})`
          );
        }

        const config: SseConfig = await response.json();
        if (aborted) {
          return;
        }
        if (!config.streamUrl || !config.accessToken) {
          throw new Error('[SSE] Stream configuration missing URL or token');
        }

        const streamUrl = new URL(config.streamUrl);
        streamUrl.searchParams.set('access_token', config.accessToken);
        if (config.impersonationSupportToken) {
          streamUrl.searchParams.set(
            'impersonation_support_token',
            config.impersonationSupportToken
          );
        }

        cleanupSource();
        source = new EventSource(streamUrl.toString());
        source.addEventListener('message', handleEvent);
        source.addEventListener('error', handleError);
        source.addEventListener('open', handleOpen);
      } catch (error) {
        console.warn('[SSE] Failed to initialize stream', error);
        if (!aborted) {
          if (reconnectTimer) {
            clearTimeout(reconnectTimer);
          }
          reconnectTimer = setTimeout(() => {
            reconnectTimer = null;
            startStream();
          }, 5000);
        }
      }
    };

    startStream();

    return () => {
      aborted = true;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
      }
      cleanupSource();
    };
  }, [
    clearActivities,
    clearAnalyses,
    clearTimelyAnalyses,
    clearWorkouts,
    setUnreadCount,
    invalidateAnalysisState,
    refreshSettings,
    refreshUnreadCount,
    invalidateChats,
    triggerRefresh,
    bumpWorkoutRefresh,
    bumpActivityRefresh,
  ]);

  return null;
}
